import React, { useState, useEffect } from 'react';
import { Award, Download, RefreshCw, AlertCircle, Filter, TrendingUp, TrendingDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { NavBar } from './NavBar';
import { ExportButtons } from './ExportButtons';
import { csvCell } from '../utils/csvSafe';


// ─── Types ────────────────────────────────────────────────────────────────────

interface ScorecardRow {
  facility_code: string;
  facility_name: string;
  facility_type: string;
  facility_class: string;
  total_inbound_tons: string;
  company_inbound_tons: string;
  independent_inbound_tons: string;
  transfer_inbound_tons: string;
  total_outbound_tons: string;
  disposed_tons: string;
  diverted_tons: string;
  diversion_rate: string;
  mass_balance_variance_tons: string;
  primary_outbound_facility: string;
  primary_outbound_facility_name: string;
  primary_outbound_tons: string;
}

interface Filters {
  year: string;
  month: string;
  facility: string;
}

const MONTHS = [
  { value: '1',  label: 'January'   }, { value: '2',  label: 'February'  },
  { value: '3',  label: 'March'     }, { value: '4',  label: 'April'     },
  { value: '5',  label: 'May'       }, { value: '6',  label: 'June'      },
  { value: '7',  label: 'July'      }, { value: '8',  label: 'August'    },
  { value: '9',  label: 'September' }, { value: '10', label: 'October'   },
  { value: '11', label: 'November'  }, { value: '12', label: 'December'  },
];

const now = new Date();
const CURRENT_YEAR = String(now.getFullYear());
const CURRENT_MONTH = String(now.getMonth() + 1);

function toNum(s: string | undefined): number {
  if (!s) return 0;
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

function FacilityCard({ row }: { row: ScorecardRow }) {
  const diversionRate = toNum(row.diversion_rate);
  const variance = toNum(row.mass_balance_variance_tons);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
      <div className="mb-3">
        <h3 className="text-sm font-bold text-gray-900">{row.facility_name}</h3>
        <p className="text-xs text-gray-400">{row.facility_type}{row.facility_class ? ` · ${row.facility_class}` : ''}</p>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <p className="text-xs text-gray-500">Total Inbound</p>
          <p className="text-lg font-bold text-gray-900">{toNum(row.total_inbound_tons).toLocaleString('en-US', { maximumFractionDigits: 1 })}<span className="text-xs font-normal text-gray-400 ml-1">tons</span></p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Total Outbound</p>
          <p className="text-lg font-bold text-gray-900">{toNum(row.total_outbound_tons).toLocaleString('en-US', { maximumFractionDigits: 1 })}<span className="text-xs font-normal text-gray-400 ml-1">tons</span></p>
        </div>
      </div>

      <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 mb-3">
        <span className="text-xs font-medium text-gray-600">Diversion Rate</span>
        <span className={`text-sm font-bold flex items-center gap-1 ${diversionRate >= 50 ? 'text-green-600' : 'text-orange-600'}`}>
          {diversionRate >= 50 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
          {row.diversion_rate ? `${diversionRate.toFixed(1)}%` : '—'}
        </span>
      </div>

      <div className="space-y-1.5 text-xs text-gray-500">
        <div className="flex justify-between"><span>Disposed</span><span className="font-medium text-gray-700">{toNum(row.disposed_tons).toLocaleString('en-US', { maximumFractionDigits: 1 })} tons</span></div>
        <div className="flex justify-between"><span>Diverted</span><span className="font-medium text-gray-700">{toNum(row.diverted_tons).toLocaleString('en-US', { maximumFractionDigits: 1 })} tons</span></div>
        <div className="flex justify-between"><span>Mass Balance Variance</span><span className="font-medium text-gray-700">{variance.toLocaleString('en-US', { maximumFractionDigits: 1 })} tons</span></div>
        {row.primary_outbound_facility_name && (
          <div className="flex justify-between pt-1.5 border-t border-gray-100 mt-1.5">
            <span>Primary Destination</span>
            <span className="font-medium text-gray-700">{row.primary_outbound_facility_name}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function FacilityScorecard() {
  const { getToken } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL ?? '';

  const [filters, setFilters] = useState<Filters>({ year: CURRENT_YEAR, month: CURRENT_MONTH, facility: '' });
  const [facilities, setFacilities] = useState<{ facility_code: string; facility_name: string }[]>([]);
  const [data, setData] = useState<ScorecardRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_URL}/reports/generate`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ report_id: 'lookup.facilities', params: {} }),
        });
        const result = await res.json();
        setFacilities(result.data ?? []);
      } catch (e) {
        console.error('Failed to load facilities:', e);
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const params: Record<string, string> = { year: filters.year, month: filters.month };
      if (filters.facility) params.facility = filters.facility;

      const res = await fetch(`${API_URL}/reports/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ report_id: 'dashboard.facility_scorecard', params }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).error ?? `HTTP ${res.status}`);
      }
      const result = await res.json();
      setData(result.data ?? []);
      setHasLoaded(true);
    } catch (e: any) {
      setError(e.message ?? 'Unexpected error');
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    if (!data.length) return;
    const headers = Object.keys(data[0]);
    const rows = data.map(r => headers.map(h => csvCell((r as any)[h])).join(','));
    const csv = [headers.map(csvCell).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `facility-scorecard-${filters.year}-${filters.month.padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />

      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-amber-600 rounded-lg">
              <Award className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Facility Performance Scorecard</h1>
              <p className="text-sm text-gray-500">Per-facility KPI summary for the selected period</p>
            </div>
          </div>
					{hasLoaded && data.length > 0 && (
					  <div className="flex items-center space-x-2">
					    <button onClick={exportCSV}
					      className="flex items-center space-x-2 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors">
					      <Download className="w-4 h-4" /><span>Export CSV</span>
					    </button>
					    <ExportButtons
					      reportTitle="Facility Performance Scorecard"
					      subtitle={`${MONTHS.find(m => m.value === filters.month)?.label ?? ''} ${filters.year}`}
					      getExportData={() => ({ columns: Object.keys(data[0]), data: data as unknown as Record<string, string>[] })}
					    />
					  </div>
					)}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center space-x-2 mb-4">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Filters</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Year <span className="text-red-500">*</span></label>
              <input type="number" value={filters.year} min="2020" max={Number(CURRENT_YEAR)}
                onChange={e => setFilters(f => ({ ...f, year: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Month <span className="text-red-500">*</span></label>
              <select value={filters.month} onChange={e => setFilters(f => ({ ...f, month: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500">
                {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Facility</label>
              <select value={filters.facility} onChange={e => setFilters(f => ({ ...f, facility: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500">
                <option value="">All Facilities</option>
                {facilities.map(fc => <option key={fc.facility_code} value={fc.facility_code}>{fc.facility_name || fc.facility_code}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={fetchReport} disabled={loading || !filters.year || !filters.month}
              className="flex items-center space-x-2 px-6 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>{loading ? 'Loading…' : 'Run Report'}</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-800">Failed to load report</p>
              <p className="text-sm text-red-600 mt-0.5">{error}</p>
            </div>
          </div>
        )}

        {hasLoaded && (
          data.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm text-center py-16">
              <Award className="w-12 h-12 mx-auto mb-3 text-gray-200" />
              <p className="text-sm font-medium text-gray-400">No data found for the selected filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.map(row => <FacilityCard key={row.facility_code} row={row} />)}
            </div>
          )
        )}

        {!hasLoaded && !loading && !error && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm text-center py-20">
            <Award className="w-16 h-16 mx-auto mb-4 text-gray-200" />
            <p className="text-base font-medium text-gray-500">Select a period and click Run Report</p>
            <p className="text-sm text-gray-400 mt-1">Year and Month are required</p>
          </div>
        )}
      </main>
    </div>
  );
}
